import type {
  ToolCallMessagePartComponent,
  ToolCallMessagePartProps,
} from '@assistant-ui/react'
import type { ToolArtifact } from '@web/adapter/toThreadMessages'
import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import type { AppToolPresentation } from '@web/api/types'
import { McpConnectionRequest } from '@web/components/McpConnections'
import { AnalysisArtifactTool } from '@web/components/tools/AnalysisArtifactTool'
import { GreetingArtifactTool } from '@web/components/tools/GreetingArtifactTool'
import {
  AuthoredFields,
  readableResult,
  TechnicalDetails,
} from '@web/components/tools/StructuredContent'
import { ToolResultLinks } from '@web/components/tools/ToolResultLinks'
import { ToolShell, toolState } from '@web/components/tools/ToolShell'
import {
  AgentTool,
  BashTool,
  EditTool,
  GenericTool,
  ReadableOutput,
  ReadTool,
  SearchTool,
  TodoTool,
  ToolContent,
  WebTool,
  WriteTool,
} from '@web/components/tools/tools'

const COMMON_RENDERERS: Record<string, ToolCallMessagePartComponent> = {
  Bash: BashTool,
  Read: ReadTool,
  Edit: EditTool,
  MultiEdit: EditTool,
  NotebookEdit: EditTool,
  Write: WriteTool,
  Grep: SearchTool,
  Glob: SearchTool,
  Agent: AgentTool,
  Task: AgentTool,
  TodoWrite: TodoTool,
  TaskCreate: TodoTool,
  TaskUpdate: TodoTool,
  TaskList: TodoTool,
  TaskGet: TodoTool,
  WebFetch: WebTool,
  WebSearch: WebTool,
}

const KIND_RENDERERS: Record<string, ToolCallMessagePartComponent> = {
  execute: BashTool,
  read: ReadTool,
  edit: EditTool,
  search: SearchTool,
  fetch: WebTool,
}

/** Both transports and nested transcripts resolve through the same trusted presentation path. */
export function ToolRenderer(props: ToolCallMessagePartProps) {
  const artifact = props.artifact as ToolArtifact | undefined
  const bridge = useOptionalBridge()
  const declarations =
    bridge?.snapshot.bridge.record?.config.app_configuration?.rendering
      ?.tools ?? []
  const matches = declarations.filter((entry) => entry.name === props.toolName)
  const presentation =
    artifact?.presentation ?? (matches.length === 1 ? matches[0] : undefined)
  const state = toolState(
    artifact,
    props.result,
    props.isError,
    bridge?.snapshot.acp.running ?? true,
  )
  const Renderer =
    COMMON_RENDERERS[props.toolName] ??
    (artifact?.kind ? KIND_RENDERERS[artifact.kind] : undefined) ??
    GenericTool
  return (
    <div
      className="tool-call min-w-0"
      data-tool-call-id={props.toolCallId}
      data-tool-status={state}
    >
      {presentation?.renderer === 'analysis' ? (
        <AnalysisArtifactTool {...props} presentation={presentation} />
      ) : presentation ? (
        <AppTool {...props} presentation={presentation} />
      ) : (
        <Renderer {...props} />
      )}
      <McpConnectionRequest result={props.result} toolName={props.toolName} />
      <ToolResultLinks result={props.result} />
    </div>
  )
}

function AppTool(
  props: ToolCallMessagePartProps & { presentation: AppToolPresentation },
) {
  const { presentation } = props
  const artifact = props.artifact as ToolArtifact | undefined
  const bridge = useOptionalBridge()
  const state = toolState(
    artifact,
    props.result,
    props.isError,
    bridge?.snapshot.acp.running ?? true,
  )
  const heading =
    state === 'running' || state === 'pending'
      ? (presentation.running_label ?? presentation.title)
      : state === 'done'
        ? (presentation.completed_label ?? presentation.title)
        : presentation.title
  const result = readableResult(props.result)
  const savedCard =
    state === 'done' &&
    presentation.renderer === 'mcp_app' &&
    artifact?.appTool?.verified &&
    [
      'ui://hello-world/greeting-card',
      'ui://hello-world/greeting-detail',
    ].includes(artifact.appTool.resourceUri)
  return (
    <ToolShell
      icon="tool"
      name={heading}
      artifact={artifact}
      result={props.result}
      isError={props.isError}
    >
      <AuthoredFields value={props.args} fields={presentation.input_fields} />
      {(state !== 'done' || !presentation.result_fields?.length) && (
        <ToolContent artifact={artifact} />
      )}
      {state === 'done' && (
        <div className="mt-2">
          {presentation.result_fields?.length ? (
            <AuthoredFields
              value={result}
              fields={presentation.result_fields}
            />
          ) : (
            <ReadableOutput result={props.result} />
          )}
        </div>
      )}
      {state === 'error' && (
        <div className="mt-2 text-danger">
          <ReadableOutput result={props.result} />
        </div>
      )}
      {savedCard && (
        <div className="mt-3">
          <GreetingArtifactTool {...props} />
        </div>
      )}
      <TechnicalDetails args={props.args} result={props.result} />
    </ToolShell>
  )
}

/** Explicit names only; free-form ACP titles never select a built-in tool identity. */
export const COMMON_TOOL_NAMES = Object.keys(COMMON_RENDERERS)
